/**
 * UAT 播放診斷資料整理器。
 *
 * 這個檔案只負責「讀取並整理」瀏覽器、video、HLS 與授權狀態，
 * 不會控制播放、不會執行恢復，也不會把資料送到後端。
 * Learning.vue 會每秒建立即時快照，並在重要事件發生時保留異常快照。
 */

// 將瀏覽器只提供數字的 readyState 轉成可閱讀文字。
const READY_STATE_LABELS = [
  'HAVE_NOTHING',
  'HAVE_METADATA',
  'HAVE_CURRENT_DATA',
  'HAVE_FUTURE_DATA',
  'HAVE_ENOUGH_DATA',
];

// networkState 代表瀏覽器目前是否仍在取得媒體資料，不等同於網路是否斷線。
const NETWORK_STATE_LABELS = [
  'NETWORK_EMPTY',
  'NETWORK_IDLE',
  'NETWORK_LOADING',
  'NETWORK_NO_SOURCE',
];

// HTMLMediaElement.error.code 的標準錯誤名稱。
const MEDIA_ERROR_LABELS = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

function round(value, digits = 2) {
  if (!Number.isFinite(value)) return null;
  const multiplier = 10 ** digits;
  return Math.round(value * multiplier) / multiplier;
}

/**
 * 找出 currentTime 所在的 buffered 區段，並計算前方還有幾秒可播放。
 * bufferedAhead 接近 0 且播放停止時，通常偏向下載速度不足；
 * bufferedAhead 仍充足卻不前進時，則較可能是播放器或解碼器卡住。
 */
function getBufferedState(video) {
  if (!video || !video.buffered || video.buffered.length === 0) {
    return {
      aheadSeconds: 0,
      rangeCount: 0,
      currentRange: null,
    };
  }

  try {
    let currentRange = null;
    for (let index = 0; index < video.buffered.length; index += 1) {
      const start = video.buffered.start(index);
      const end = video.buffered.end(index);
      if (video.currentTime >= start - 0.1 && video.currentTime <= end + 0.1) {
        currentRange = [round(start), round(end)];
        break;
      }
    }

    return {
      aheadSeconds: currentRange ? round(Math.max(0, currentRange[1] - video.currentTime)) : 0,
      rangeCount: video.buffered.length,
      currentRange,
    };
  } catch (error) {
    return {
      aheadSeconds: null,
      rangeCount: null,
      currentRange: null,
    };
  }
}

/**
 * 診斷資訊可能會被截圖或複製，因此只保留來源的網域與路徑。
 * query string 可能含簽章或一次性資訊，blob URL 也不具除錯價值，皆不輸出原值。
 */
function sanitizeMediaSource(source) {
  if (!source) return '';
  if (source.indexOf('blob:') === 0) return 'blob:MediaSource';

  try {
    const parsedUrl = new URL(source, window.location.href);
    // 診斷畫面不顯示 query string，避免意外帶出簽章或一次性參數。
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (error) {
    return String(source).split('?')[0];
  }
}

// 判斷目前是桌面常見的 HLS.js/MSE，還是 iOS/WebKit 的原生 HLS 管線。
function getPlaybackMode(video, hls) {
  if (hls) return 'HLS.js / MSE';
  if (video && video.currentSrc && video.currentSrc.indexOf('blob:') === 0) {
    return 'MSE blob（HLS instance 未連結）';
  }
  if (video && video.currentSrc) return '瀏覽器／iOS 原生 HLS';
  return '尚未初始化';
}

/**
 * 取得瀏覽器願意提供的連線提示值。
 * Network Information API 並非所有瀏覽器都有（特別是 iOS），缺少時保留 null，
 * 避免把「瀏覽器不支援此 API」誤判成連線異常。
 */
function getConnectionState() {
  const connection = navigator.connection
    || navigator.mozConnection
    || navigator.webkitConnection;

  if (!connection) {
    return {
      online: navigator.onLine,
      effectiveType: null,
      downlinkMbps: null,
      rttMs: null,
      saveData: null,
    };
  }

  return {
    online: navigator.onLine,
    effectiveType: connection.effectiveType || connection.type || null,
    downlinkMbps: round(connection.downlink),
    rttMs: Number.isFinite(connection.rtt) ? connection.rtt : null,
    saveData: typeof connection.saveData === 'boolean' ? connection.saveData : null,
  };
}

// 擷取 HLS.js 當下畫質、自動畫質及 media 是否仍附著等關鍵狀態。
function getHlsState(hls) {
  if (!hls) return null;

  const levels = Array.isArray(hls.levels) ? hls.levels : [];
  const activeLevel = levels[hls.currentLevel] || levels[hls.loadLevel] || null;
  return {
    currentLevel: Number.isFinite(hls.currentLevel) ? hls.currentLevel : null,
    loadLevel: Number.isFinite(hls.loadLevel) ? hls.loadLevel : null,
    nextLoadLevel: Number.isFinite(hls.nextLoadLevel) ? hls.nextLoadLevel : null,
    autoLevelEnabled: typeof hls.autoLevelEnabled === 'boolean' ? hls.autoLevelEnabled : null,
    activeHeight: activeLevel && Number.isFinite(activeLevel.height) ? activeLevel.height : null,
    availableHeights: levels
      .map((level) => level.height)
      .filter((height) => Number.isFinite(height)),
    mediaAttached: !!hls.media,
  };
}

/**
 * 授權 API 成功後只保存狀態與到期時間，不保存 Policy、Signature 或 Key。
 * remainingSeconds 每次建立快照時重新計算，便於確認黑畫面是否發生在授權到期附近。
 */
function getAuthorizationState(authorization) {
  const source = authorization || {};
  const expiresAtMs = source.expiresAt ? Date.parse(source.expiresAt) : NaN;
  const remainingSeconds = Number.isFinite(expiresAtMs)
    ? Math.floor((expiresAtMs - Date.now()) / 1000)
    : null;

  return {
    status: source.status || '尚未取得',
    refreshedAt: source.refreshedAt || null,
    expiresAt: source.expiresAt || null,
    validitySeconds: Number.isFinite(source.validitySeconds)
      ? source.validitySeconds
      : null,
    remainingSeconds,
    classCode: source.classCode || '',
    error: source.error || '',
  };
}

/**
 * 從 CloudFront signed cookie 的 Policy 解出 DateLessThan。
 * 此函式只回傳到期時間；原始 Policy 不會進入診斷面板或複製內容。
 */
export function getCloudFrontPolicyExpiration(policy) {
  if (!policy || typeof policy !== 'string') return null;

  try {
    // CloudFront signed cookie 使用自訂的 URL-safe Base64 字元替換。
    let normalized = policy
      .replace(/-/g, '+')
      .replace(/_/g, '=')
      .replace(/~/g, '/');
    while (normalized.length % 4 !== 0) normalized += '=';

    const decodedPolicy = JSON.parse(window.atob(normalized));
    const statements = decodedPolicy.Statement;
    const statement = Array.isArray(statements) ? statements[0] : statements;
    const dateLessThan = statement
      && statement.Condition
      && statement.Condition.DateLessThan;
    const epochTime = dateLessThan && Number(dateLessThan['AWS:EpochTime']);
    if (!Number.isFinite(epochTime)) return null;
    return new Date(epochTime * 1000).toISOString();
  } catch (error) {
    return null;
  }
}

/**
 * 將不同 hls.js 版本的 ERROR payload 統一成面板可顯示的格式。
 * 會保留 fatal、HTTP code、錯誤類型與訊息，但媒體網址仍會先經過安全處理。
 */
export function createHlsErrorDiagnostic(data = {}) {
  const response = data.response || {};
  const fragment = data.frag || {};
  const context = data.context || {};
  // hls.js 0.14.x 的 internalException 使用 err；新版多數錯誤使用 error。
  const error = data.error || data.err || {};

  return {
    at: new Date().toISOString(),
    type: data.type || '',
    details: data.details || '',
    fatal: !!data.fatal,
    event: data.event || '',
    responseCode: Number.isFinite(response.code) ? response.code : null,
    responseText: response.text || '',
    source: sanitizeMediaSource(fragment.url || context.url || response.url || ''),
    message: error.message || (typeof error === 'string' ? error : ''),
    stack: error.stack ? String(error.stack).slice(0, 1200) : '',
  };
}

/**
 * 建立某一時間點的完整播放快照。
 *
 * 快照分成六類：課程、原生 video、HLS、授權、連線／頁面及播放器恢復狀態。
 * 畫面上的「目前狀態」與「異常當下快照」都使用相同結構，方便直接比較。
 */
export function createPlaybackDiagnosticSnapshot({
  video,
  hls,
  course,
  authorization,
  lastHlsError,
  playbackIntended,
  hasError,
  mediaWasEmptied,
  isRecovering,
  stalledSince,
}) {
  const buffered = getBufferedState(video);
  const mediaError = video && video.error;
  const stalledForSeconds = stalledSince
    ? round((Date.now() - stalledSince) / 1000)
    : 0;

  return {
    capturedAt: new Date().toISOString(),
    // 用 classCode 對照是哪一堂課，不依賴畫面上可能被截斷的課程名稱。
    course: {
      name: course && course.className ? course.className : '',
      code: course && course.classCode ? course.classCode : '',
    },
    playbackMode: getPlaybackMode(video, hls),
    // 原生 video 是 iOS 原生播放與 HLS.js 最終共同依賴的媒體元素。
    media: video ? {
      readyState: video.readyState,
      readyStateLabel: READY_STATE_LABELS[video.readyState] || 'UNKNOWN',
      networkState: video.networkState,
      networkStateLabel: NETWORK_STATE_LABELS[video.networkState] || 'UNKNOWN',
      paused: video.paused,
      ended: video.ended,
      seeking: video.seeking,
      currentTime: round(video.currentTime),
      duration: round(video.duration),
      playbackRate: round(video.playbackRate),
      width: video.videoWidth || 0,
      height: video.videoHeight || 0,
      source: sanitizeMediaSource(video.currentSrc || video.src || ''),
      errorCode: mediaError ? mediaError.code : null,
      errorLabel: mediaError ? MEDIA_ERROR_LABELS[mediaError.code] || 'UNKNOWN' : '',
      errorMessage: mediaError && mediaError.message ? mediaError.message : '',
      buffered,
    } : null,
    // iOS 原生 HLS 沒有 hls instance，此欄位會是 null，屬正常情況。
    hls: getHlsState(hls),
    authorization: getAuthorizationState(authorization),
    connection: getConnectionState(),
    document: {
      visibilityState: document.visibilityState,
      hidden: document.hidden,
    },
    // 這些是應用程式自己的狀態，用來區分「使用者暫停」和「想播放但被迫停止」。
    player: {
      playbackIntended: !!playbackIntended,
      hasError: !!hasError,
      mediaWasEmptied: !!mediaWasEmptied,
      isRecovering: !!isRecovering,
      stalledForSeconds,
    },
    // User-Agent 對 iPadOS、瀏覽器版本及 WebKit 問題的交叉比對很重要。
    device: {
      userAgent: navigator.userAgent,
      platform: navigator.platform || '',
      language: navigator.language || '',
    },
    // 只保留最新一筆 HLS 錯誤；完整事件先後順序由面板的 events 顯示。
    lastHlsError: lastHlsError || null,
  };
}
